// =============================================================================
// Library Category Detail Page — category info + linked-tag management + assets
// =============================================================================
// Header: name/slug/counts + breadcrumb through the ancestor chain. Linked
// tags: Combobox to link (library:admin), chip list with one-click unlink.
// Below: the asset grid for the category using the EXTENDED filter (direct
// members + assets carrying a linked tag). Tag-matched assets show a
// "via tag: X" badge — implicit membership must be visible. The shared bulk
// bar works on the listed set (the "move assets, then delete" flow).
// =============================================================================

import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Combobox,
  Spinner,
} from '@tkpod/ui';
import type { ComboboxOption } from '@tkpod/ui';
import type { LibraryAssetSummary } from '@tkpod/shared';
import { useAuth } from '@/hooks/use-auth';
import { toast } from '@/lib/toast';
import {
  fetchCategories,
  fetchCategoryDetail,
  fetchTags,
  libraryKeys,
  linkCategoryTag,
  searchAssets,
  unlinkCategoryTag,
  updateCategory,
} from './api';
import { LibraryBulkBar } from './LibraryBulkBar';

const PAGE_SIZE = 24;

function statusVariant(status: string): 'success' | 'warning' | 'danger' | 'default' {
  if (status === 'READY') return 'success';
  if (status === 'PROCESSING') return 'warning';
  if (status === 'FAILED') return 'danger';
  return 'default';
}

export function LibraryCategoryDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canAdmin = (user?.permissions ?? []).includes('library:admin');

  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Map<string, string>>(new Map());

  const detailQuery = useQuery({
    queryKey: libraryKeys.category(id),
    queryFn: () => fetchCategoryDetail(id),
    enabled: !!id,
  });
  const categoriesQuery = useQuery({ queryKey: libraryKeys.categories, queryFn: fetchCategories });
  const tagsQuery = useQuery({ queryKey: libraryKeys.tags, queryFn: fetchTags });

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    p.set('categoryId', id);
    p.set('page', String(page));
    p.set('pageSize', String(PAGE_SIZE));
    return p.toString();
  }, [id, page]);

  const assetsQuery = useQuery({
    queryKey: libraryKeys.assets({ categoryId: id, page, pageSize: PAGE_SIZE }),
    queryFn: () => searchAssets(qs),
    enabled: !!id,
  });

  const detail = detailQuery.data;
  const items = assetsQuery.data?.items ?? [];
  const total = assetsQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const linkedIds = useMemo(
    () => new Set((detail?.linkedTags ?? []).map((t) => t.id)),
    [detail],
  );
  const linkableOptions: ComboboxOption[] = useMemo(
    () =>
      (tagsQuery.data ?? [])
        .filter((t) => !linkedIds.has(t.id))
        .map((t) => ({ value: t.id, label: t.name })),
    [tagsQuery.data, linkedIds],
  );

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['library'] });
  };

  const linkMutation = useMutation({
    mutationFn: (tagId: string) => linkCategoryTag(id, tagId),
    onSuccess: () => {
      toast('Tag linked — its assets are now listed in this category', 'success');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const unlinkMutation = useMutation({
    mutationFn: (tagId: string) => unlinkCategoryTag(id, tagId),
    onSuccess: () => {
      toast('Tag unlinked', 'success');
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const galleryMutation = useMutation({
    mutationFn: (galleryVisible: boolean) => updateCategory(id, { galleryVisible }),
    onSuccess: (cat) => {
      toast(
        cat.galleryVisible
          ? 'Category is now visible in the designer gallery'
          : 'Category is now hidden from the designer gallery (including sub-categories)',
        'success',
      );
      invalidate();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const toggleSelect = (a: LibraryAssetSummary) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(a.id)) next.delete(a.id);
      else next.set(a.id, a.status);
      return next;
    });
  const hasFailedSelected = [...selected.values()].some((s) => s === 'FAILED');

  if (detailQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner size="lg" label="Loading category…" />
      </div>
    );
  }
  if (detailQuery.isError || !detail) {
    return (
      <Card>
        <div className="flex flex-col items-center gap-3 py-12">
          <p className="text-sm text-red-400">Failed to load category.</p>
          <Button variant="outline" size="sm" onClick={() => navigate('/library/taxonomy')}>
            Back to Categories & Tags
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb through the ancestor chain */}
      <nav className="flex flex-wrap items-center gap-2 text-sm text-text-tertiary">
        <Link to="/library/taxonomy" className="hover:text-text-primary transition-colors">
          Categories & Tags
        </Link>
        {detail.parents.map((p) => (
          <span key={p.id} className="flex items-center gap-2">
            <span>/</span>
            <Link
              to={`/library/categories/${p.id}`}
              className="hover:text-text-primary transition-colors"
            >
              {p.name}
            </Link>
          </span>
        ))}
        <span>/</span>
        <span className="text-text-primary">{detail.name}</span>
      </nav>

      {/* Category info + linked tags */}
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <CardHeader
              title={detail.name}
              description={`${detail.assetCount} direct asset(s) · ${detail.childCount} sub-categor${detail.childCount === 1 ? 'y' : 'ies'} · slug: ${detail.slug}`}
            />
            {!detail.galleryVisible && (
              <Badge variant="warning" size="sm">hidden from gallery</Badge>
            )}
          </div>

          {/* Category-level gallery switch — hiding also hides all
              sub-categories' assets (enforced by the gallery query). */}
          {canAdmin && (
            <Button
              size="sm"
              variant="outline"
              loading={galleryMutation.isPending}
              onClick={() => galleryMutation.mutate(!detail.galleryVisible)}
            >
              {detail.galleryVisible ? 'Hide from gallery' : 'Show in gallery'}
            </Button>
          )}
        </div>

        <div className="mt-4">
          <h4 className="text-sm font-semibold text-text-primary">Linked tags</h4>
          <p className="mt-0.5 text-xs text-text-tertiary">
            Assets carrying a linked tag are listed in this category automatically
            (marked with a “via tag” badge). Tree counts stay direct-only.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            {detail.linkedTags.length === 0 && (
              <span className="text-sm text-text-tertiary">No linked tags.</span>
            )}
            {detail.linkedTags.map((t) => (
              <span
                key={t.id}
                className="inline-flex items-center gap-1.5 rounded-full border border-brand-800 bg-brand-950 px-2.5 py-1 text-xs text-white/90"
              >
                {t.name}
                <span className="text-white/55">({t.assetCount ?? 0})</span>
                {canAdmin && (
                  <button
                    type="button"
                    aria-label={`Unlink ${t.name}`}
                    className="text-white/55 transition-colors hover:text-red-400"
                    onClick={() => unlinkMutation.mutate(t.id)}
                  >
                    ×
                  </button>
                )}
              </span>
            ))}
            {canAdmin && (
              <div className="w-56">
                <Combobox
                  size="sm"
                  options={linkableOptions}
                  value={null}
                  onChange={(tagId) => {
                    if (tagId) linkMutation.mutate(tagId);
                  }}
                  placeholder="Link a tag…"
                  searchPlaceholder="Search tags…"
                  allowClear={false}
                />
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Assets in this category (direct + via linked tags) */}
      <Card>
        <CardHeader
          title="Assets"
          description={`${total} asset(s) — direct members and assets carrying a linked tag.`}
        />

        <div className="mt-4">
          {assetsQuery.isLoading ? (
            <div className="flex justify-center py-12">
              <Spinner size="md" label="Loading assets…" />
            </div>
          ) : assetsQuery.isError ? (
            <p className="py-8 text-center text-sm text-red-400">Failed to load assets.</p>
          ) : items.length === 0 ? (
            <p className="py-8 text-center text-sm text-text-tertiary">
              No assets in this category yet.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {items.map((a) => (
                <div
                  key={a.id}
                  className={`group relative overflow-hidden rounded-lg border ${selected.has(a.id) ? 'border-brand-500' : 'border-brand-900'} bg-surface-secondary`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(a.id)}
                    onChange={() => toggleSelect(a)}
                    className="absolute left-2 top-2 z-10"
                  />
                  <button
                    type="button"
                    onClick={() => navigate(`/library/assets/${a.id}`)}
                    className="block w-full"
                  >
                    <div className="flex aspect-square items-center justify-center bg-[#bdbdbd] p-1">
                      {a.thumbUrl && (
                        <img
                          src={a.thumbUrl}
                          alt={a.sku}
                          className="max-h-full max-w-full object-contain"
                        />
                      )}
                    </div>
                    <div className="p-2 text-left">
                      <p className="truncate text-xs font-medium text-text-primary">{a.sku}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        <Badge variant={statusVariant(a.status)} size="sm">{a.status}</Badge>
                        {a.viaTagName && (
                          <Badge variant="primary" size="sm">via tag: {a.viaTagName}</Badge>
                        )}
                        {!a.galleryVisible && (
                          <Badge variant="warning" size="sm">hidden</Badge>
                        )}
                      </div>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="mt-4 flex items-center justify-between text-sm text-text-secondary">
          <span>{total} asset(s)</span>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Prev
            </Button>
            <span>Page {page} / {totalPages}</span>
            <Button
              variant="ghost"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </Card>

      {/* Shared bulk bar — the "reassign assets, then delete the category" flow */}
      <LibraryBulkBar
        selectedIds={[...selected.keys()]}
        categories={categoriesQuery.data ?? []}
        tags={tagsQuery.data ?? []}
        hasFailedSelected={hasFailedSelected}
        showArchive
        onDone={() => setSelected(new Map())}
      />
    </div>
  );
}
